import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ReplaySubject, Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { ReadSequencer, SequencedRead } from './read-sequencer';

// How many times a vendor-started read is sent again after a merge overtakes it.
const MERGE_RESENDS = 3;

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private projectDataSubject: ReplaySubject<any> = new ReplaySubject<any>(1);
  private showClearSearchButtonSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  showClearSearchButton = this.showClearSearchButtonSubject.asObservable();
  private projectDetails: any;
  private dataLoaded = false;
  private currentSearchTerm = 'none';
  private currentAddSearchTerm = false;
  private mergeSequence = 0;
  private readonly reads = new ReadSequencer();

  constructor(private http: HttpClient) {}

  public fetchData(): Observable<any> {
    if (!this.dataLoaded) {
      this.fetch('none', false, MERGE_RESENDS);
    }
    return this.projectDataSubject.asObservable();
  }

  public refetchData(): Observable<any> {
    this.fetch('none', false, MERGE_RESENDS);
    return this.projectDataSubject.asObservable();
  }

  // No resend here: the caller is told `false` and owns the retry
  // (BatchDeclineService resends under the products' lock). The read starts on
  // subscribe, and unsubscribing before it settles cancels the HTTP request.
  public refetchCurrent(): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const read = this.fetch(this.currentSearchTerm, this.currentAddSearchTerm, 0);
      const forwarding = read.result.subscribe(subscriber);
      return () => {
        forwarding.unsubscribe();
        read.cancel();
      };
    });
  }

  /**
   * Whether the feed is filtered by a vendor search. The backend's search runs
   * per site, so a product can be missing from a project that is still listed.
   */
  public get searchActive(): boolean {
    return this.currentAddSearchTerm;
  }

  public fetchSearchData(searchTerm: string, addSearchTerm: boolean) {
    this.fetch(searchTerm, addSearchTerm, MERGE_RESENDS);
  }

  public clearSearchData() {
    this.fetch('none', false, MERGE_RESENDS);
  }

  public updateProjectDataSubject(updatedProjectDetail: any) {
    if (!this.dataLoaded) {
      return;
    }
    const updatedDetails = this.projectDetails.map((projectDetail: any) => {
      if (updatedProjectDetail.projectId === projectDetail.projectId) {
        let updatedProjectDetailSites = updatedProjectDetail.sites;
        // Field-level carry-forward, run before the site-level append below:
        // the entries the append adds are the cached SiteDetail references
        // themselves and already hold their own visit. Written in place on the
        // caller's own array rather than through a `map` — this merge's `push`
        // is load-bearing, because project-overview-container reads
        // `this.data.sites` straight back after calling us to recompute its
        // button flags, and a fresh array would stop it seeing the appended
        // sites.
        updatedProjectDetailSites.forEach(
          (updatedSiteDetail: any, index: number) => {
            updatedProjectDetailSites[index] = this.withCarriedSiteVisit(
              updatedSiteDetail,
              projectDetail.sites?.find(
                (s: any) => s.proposal?.id === updatedSiteDetail?.proposal?.id
              )
            );
          }
        );
        projectDetail.sites.forEach((siteDetail: any) => {
          if (
            !updatedProjectDetailSites.find(
              (s: any) => s.proposal.id === siteDetail.proposal.id
            )
          ) {
            updatedProjectDetailSites.push(siteDetail);
          }
        });
        return {
          ...updatedProjectDetail,
          sites: updatedProjectDetailSites,
        };
      } else {
        return projectDetail;
      }
    });
    this.publishMergedData(updatedDetails);
  }

  public updateProjectDataSubjectWithProjectDetail(updatedProjectDetail: any) {
    if (!this.dataLoaded) {
      return;
    }
    const updatedDetails = this.projectDetails.map((projectDetail: any) => {
      if (updatedProjectDetail.projectId === projectDetail.projectId) {
        // can't replace project detail with updated project details,
        // need to merge the sites from current and updated details to make sure previously
        // actioned sites are not overridden
        const updatedSiteDetails = projectDetail.sites?.map(
          (siteDetail: any) => {
            const updatedSiteDetail = updatedProjectDetail.sites.find(
              (detail: any) => detail.site.id === siteDetail.site.id
            );
            if (updatedSiteDetail) {
              return this.withCarriedSiteVisit(updatedSiteDetail, siteDetail);
            } else {
              return siteDetail;
            }
          }
        );
        // need to map exiting and find the new one and return that...
        return {
          ...updatedProjectDetail,
          sites: updatedSiteDetails,
        };
      } else {
        return projectDetail;
      }
    });
    this.publishMergedData(updatedDetails);
  }

  /*
   * when navigating to the detail pages, the data passed in is in the shape of:
   * {projectId: UUID, ...etc., sites: [{id: UUID, ...etc.}]}
   */
  public updateProjectDataSubjectWithSingleSite(updatedProjectDetail: any) {
    if (!this.dataLoaded) {
      return;
    }
    const updatedSiteDetail = updatedProjectDetail.sites[0];
    const updatedDetails = this.projectDetails.map((projectDetail: any) => {
      if (updatedProjectDetail.projectId === projectDetail.projectId) {
        const updatedSiteDetails = projectDetail.sites.map(
          (siteDetail: any) => {
            if (updatedSiteDetail.site.id === siteDetail.site.id) {
              return this.withCarriedSiteVisit(updatedSiteDetail, siteDetail);
            } else {
              return siteDetail;
            }
          }
        );
        return { ...updatedProjectDetail, sites: updatedSiteDetails };
      } else {
        return projectDetail;
      }
    });
    this.publishMergedData(updatedDetails);
  }

  private publishMergedData(updatedDetails: any): void {
    this.projectDetails = updatedDetails;
    // Mark reads already in flight as older than this merge before emitting:
    // a subscriber may start a new read, which must not count as older.
    ++this.mergeSequence;
    this.projectDataSubject.next(updatedDetails);
  }

  public cachedProject(projectId: string): any | undefined {
    return this.projectDetails?.find((project: any) => project.projectId === projectId);
  }

  public cachedStatuses(proposalIds: readonly string[]): Map<string, string | null> {
    const wanted = new Set(proposalIds);
    const statuses = new Map<string, string | null>();
    for (const project of this.projectDetails ?? []) {
      for (const detail of project.sites ?? []) {
        const proposal = detail?.proposal;
        if (proposal && wanted.has(proposal.id)) {
          statuses.set(proposal.id, proposal.status ?? null);
        }
      }
    }
    return statuses;
  }

  /**
   * Carry a held `siteVisit` summary onto the SiteDetail an action response is
   * replacing. Used by all three merges above.
   *
   * `siteVisit` is a SiteDetail-level sibling of `site` and `proposal`, and it is
   * attached by the aggregated feed alone — `ProjectHandler.getAggregatedData`
   * reads the vendor's negotiations in one extra query and hangs a summary off
   * each product. No other producer of a SiteDetail has a visit to give. The
   * action endpoints the merges above feed from therefore hand back a
   * `{ site, proposal }` pair with no visit, and a whole-object replace would
   * silently drop the summary — the product-progress stepper would fall back to
   * "Schedule site visit" right after the vendor acted, until the next full
   * refetch. `site` and `proposal` carry no such exposure: the merges *match* on
   * them, so a response that omitted either would not merge at all.
   *
   * The rules, in the order they are applied:
   *  - a response that genuinely carries a visit wins — it is the newer read;
   *  - a nullish incoming visit means "not attached", never "no visit". The feed
   *    is the only writer, so an action response is in no position to assert
   *    absence, and the held value survives;
   *  - the carry is gated on **proposal** identity, not on the `site.id` two of
   *    the callers match on. The summary is filed per proposal
   *    (`SiteVisitProduct.proposalId`), so a replacement that swaps in a
   *    different product must not inherit the previous product's appointment.
   *
   * Returns `incoming` untouched whenever there is nothing to carry, so the
   * merges keep handing on the exact object references they were given.
   */
  private withCarriedSiteVisit(incoming: any, existing: any): any {
    if (!incoming || !existing) {
      return incoming;
    }
    if (incoming.siteVisit !== null && incoming.siteVisit !== undefined) {
      return incoming;
    }
    if (existing.siteVisit === null || existing.siteVisit === undefined) {
      return incoming;
    }
    const incomingProposalId = incoming.proposal?.id;
    if (!incomingProposalId || incomingProposalId !== existing.proposal?.id) {
      return incoming;
    }
    return { ...incoming, siteVisit: existing.siteVisit };
  }

  /**
   * Cache-only draft patch — applies `patch` (e.g. `{ bid }` / `{ proposedTurnTime }`)
   * to the cached proposal with `proposalId` WITHOUT re-emitting the subject.
   * Keeps an in-progress quote draft in the in-memory cache so it survives a
   * navigate-away/return (`fetchData` is `dataLoaded`-guarded and replays the
   * ReplaySubject's buffered cache rather than refetching) and a later
   * single-site submit merge (which reuses these cached `siteDetail`
   * references). No `.next()` on purpose: re-emitting would rebuild the list and
   * interrupt the vendor's typing in sibling rows.
   */
  public patchProposalDraftInCache(proposalId: string, patch: any): void {
    if (!this.projectDetails || !proposalId) {
      return;
    }
    for (const projectDetail of this.projectDetails) {
      for (const siteDetail of projectDetail?.sites ?? []) {
        if (siteDetail?.proposal?.id === proposalId) {
          Object.assign(siteDetail.proposal, patch);
          return;
        }
      }
    }
  }

  public toggleClearSearchButton(toggle: boolean) {
    this.showClearSearchButtonSubject.next(toggle);
  }

  /**
   * One read of the feed. Overlapping reads are ordered by `ReadSequencer`: a
   * read overtaken by a newer one reports the newest read's result instead of
   * applying its own. The exception is a first load: while nothing has
   * loaded, an older success still applies, so a failed newer first load
   * cannot leave the page empty. A response that a merge overtook would put
   * back pre-merge data, so it is not applied; the read is sent again up to
   * `mergeResends` times, then reported as dropped. `cancel` ends a read that
   * has not settled, as dropped.
   */
  private fetch(
    searchTerm: string,
    addSearchTerm: boolean,
    mergeResends: number
  ): FeedRead {
    this.currentSearchTerm = searchTerm;
    this.currentAddSearchTerm = addSearchTerm;
    const read = this.reads.start();
    const mergesAtStart = this.mergeSequence;
    let responded = false;
    let cancelResend = () => {};
    const commit = (data: any) => {
      if (this.mergeSequence === mergesAtStart) {
        this.publishFetchedData(read, data);
      } else if (mergeResends > 0) {
        const resend = this.fetch(searchTerm, addSearchTerm, mergeResends - 1);
        cancelResend = resend.cancel;
        read.forward(resend.result);
      } else {
        read.settle(false);
      }
    };
    const url = environment.apiUrl + '/project/user-projects';
    // No user identifier is sent: the backend derives the acting vendor from the
    // authenticated JWT principal and ignores any caller-supplied id.
    const params = {
      searchTerm: searchTerm,
      addSearchTerm: addSearchTerm,
    };
    const request = this.http
      .get(url, {
        params,
      })
      .subscribe({
        next: (data) => {
          responded = true;
          if (this.dataLoaded) {
            read.whenNewest(() => commit(data));
          } else {
            commit(data);
          }
        },
        error: (error) => {
          // Keep cached data intact; the request result reports reload failures,
          // while first-load/search callers have no error subscription to own them.
          console.error('Failed to fetch projects', url, error.status);
          read.whenNewest(() => read.fail(error));
        },
        complete: () => {
          // An interceptor can complete without a response (read-only alias response).
          if (!responded) {
            read.settle(false);
          }
        },
      });
    return {
      result: read.result,
      cancel: () => {
        request.unsubscribe();
        cancelResend();
        read.cancel();
      },
    };
  }

  private publishFetchedData(read: SequencedRead, data: any): void {
    // Guard the emission at the source: the API `data` array can contain a
    // nullish project-detail entry (an order/proposal the backend failed to
    // hydrate). Emitting it unfiltered makes every downstream consumer's
    // `parent.sites` deref throw `Cannot read properties of undefined`
    // during change detection. Drop holes (and tolerate a missing `data`)
    // once, here, so no consumer has to.
    this.projectDetails = Array.isArray(data?.data)
      ? data.data.filter((detail: any) => !!detail)
      : [];
    this.dataLoaded = true;
    read.overtakeOlderReads();
    this.projectDataSubject.next(this.projectDetails);
    read.settle(true);
  }
}

interface FeedRead {
  result: Observable<boolean>;
  cancel: () => void;
}
